import { useTranslation } from 'react-i18next';
import { formatDagDatum, formatDatum } from '../format';
import { useMeldToestand } from './Toestandsmelding';
import { WeekLink } from './WeekLink';
import { FOCUSRING } from '../focus';
import { STORING_MISLUKT, STORING_ONBEREIKBAAR, weektoestand } from '../toestand';
import type { Storingsoorzaak } from '../toestand';
import type { Antwoord } from '../work-quality.api';

/**
 * De drie toestanden die elke weergave van deze module deelt: laden, een storing, en een week
 * waarvoor niets is gemeten.
 *
 * Ze staan hier bij elkaar omdat het verschil ertussen de hele reden is dat deze module bestaat.
 * Een storing die als een lege week leest, zegt over iemands werk iets wat niemand heeft gemeten
 * (AD-25, AD-27); een skeleton die nullen toont, doet dat drie tellen lang. Eén plek, zodat de
 * eigen weergave, het overzicht en het persoonsdetail dat verschil niet elk apart kunnen verliezen.
 */

/**
 * Skeletons in de vorm van de verwachte tabel — nooit nullen als tussenstand.
 *
 * De vorm is geen versiering: een skeleton die één grijs vlak is, laat de pagina verspringen zodra
 * het antwoord binnenkomt, en dan verschuift de tabel onder de muis van de lezer weg. Een kopbalk en
 * vier rijen benaderen wat er komt zonder te beloven hoeveel rijen dat zijn.
 */
export function Laden() {
  const { t } = useTranslation();
  useMeldToestand(t('qa.eigen.laden'));

  return (
    <div className="flex flex-col gap-3" data-testid="werkkwaliteit-laden" role="status">
      <span className="sr-only">{t('qa.eigen.laden')}</span>
      <div
        aria-hidden="true"
        className="h-24 rounded-xl bg-[var(--background-muted)]"
        data-werkkwaliteit-skelet="blok"
      />
      <div
        aria-hidden="true"
        className="h-14 rounded-[10px] bg-[var(--background-muted)]"
        data-werkkwaliteit-skelet="blok"
      />
      <div aria-hidden="true" className="flex flex-col gap-2 rounded-xl border border-[var(--border-soft)] p-4">
        <div
          aria-hidden="true"
          className="h-4 w-2/5 rounded bg-[var(--background-muted)]"
          data-werkkwaliteit-skelet="kop"
        />
        {[0, 1, 2, 3].map((rij) => (
          <div
            aria-hidden="true"
            className="h-8 rounded bg-[var(--background-muted)]"
            data-werkkwaliteit-skelet="rij"
            key={rij}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Geen meting voor deze week — met de datum van de laatste geslaagde run, en geen nul-kaarten.
 *
 * Naast die datum staat het geplande volgende moment, en daar zijn drie zinnen voor.
 *
 * Stuurt de dienst een `volgendeRun` mee, dan noemt het scherm de dag die de planner werkelijk heeft
 * staan. Stuurt zij hem niet mee — er staat een ronde open, of het antwoord komt van een oudere
 * versie — dan valt het scherm terug op het ritme ("na afloop van deze week, op maandagochtend"),
 * want een datum verzinnen mag niet (AD-19).
 *
 * En staat de wekelijkse meting úit, dan belooft het scherm niets. Die derde zin bestaat omdat de
 * eerste twee samen niet genoeg waren: het ontbreken van `volgendeRun` betekende twee dingen — er
 * komt nooit een rapport, of er komt er binnen vijf minuten een — en dit scherm koos daar altijd de
 * hoopvolle lezing van. Op productie stond daardoor maandenlang de belofte van een maandagochtend
 * waarop niets zou gebeuren, want de meting had daar nooit gedraaid.
 *
 * **De andere helft van die dubbelzinnigheid staat er nog.** Draait de meting wél en staat er een
 * ronde open, dan zegt dit scherm nog steeds "na afloop van deze week, op maandagochtend" terwijl de
 * inhaalslag binnen vijf minuten draait voor een week die al voorbij is. Dat is een veel kleiner
 * gebrek — er kómt dan een rapport, alleen eerder dan de zin zegt — en het herzien van die twee
 * bestaande zinnen viel buiten deze wijziging. Het staat als open punt in `deferred-work.md`.
 */
export function GeenRapport({
  laatsteGeslaagdeRun,
  metingIngeschakeld = null,
  volgendeRun = null,
}: {
  laatsteGeslaagdeRun: string | null;
  /**
   * Staat de wekelijkse meting aan, zoals het antwoord het zegt?
   *
   * `null` betekent onbekend en niet uit: een oudere dienst stuurt het veld niet, en de proxy laat een
   * waarde vallen die geen ja-of-nee is. Dan gedraagt dit scherm zich als voorheen. Alleen een
   * letterlijke `false` levert de zin dat er geen rapport komt — dat is te harde taal voor een gok.
   */
  metingIngeschakeld?: boolean | null;
  /** Het geplande volgende meetmoment uit het antwoord; `null` wanneer het antwoord er geen noemt. */
  volgendeRun?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  useMeldToestand(t('qa.eigen.geenRapport'));

  // Twee dingen moeten kloppen voordat er een dag op het scherm komt, en allebei kunnen ze misgaan
  // zonder dat iemand het merkt. De tekst moet een moment zijn — `formatDagDatum` geeft een
  // onleesbare waarde ongewijzigd terug, en dan zou er "verschijnt op binnenkort" staan — en dat
  // moment moet nog moeten komen. Een planner die stilstaat blijft een datum meesturen die
  // intussen voorbij is, en een dag in het verleden aankondigen als "het volgende rapport" is
  // precies de belofte die het ritme juist vermeed. In beide gevallen valt het scherm terug op het
  // ritme, dat uit de instellingen volgt en niet uit één antwoord.
  const gepland = volgendeRun === null ? Number.NaN : Date.parse(volgendeRun);
  // De schakelaar gaat vóór de datum. Een juiste dienst kan die twee niet samen sturen — zonder
  // planner is er geen volgend moment — maar een ouder of gemengd antwoord kan het wel, en dan is de
  // schakelaar het diepere feit: een datum uit een planner die niet draait, is een overblijfsel.
  const geenMeting = metingIngeschakeld === false;
  const toonMoment = !geenMeting && Number.isFinite(gepland) && gepland > Date.now();

  return (
    <div
      className="rounded-xl border border-[var(--border-soft)] bg-[var(--background)] p-5 text-[0.8125rem] text-[var(--foreground)]"
      data-testid="werkkwaliteit-geen-rapport"
      role="status"
    >
      <p className="font-semibold">{t('qa.eigen.geenRapport')}</p>
      <p className="mt-1 text-[var(--foreground-muted)]">
        {laatsteGeslaagdeRun === null
          ? t('qa.eigen.geenRapportZonderRun')
          : t('qa.eigen.geenRapportLaatsteRun', { datum: formatDatum(laatsteGeslaagdeRun, locale) })}
      </p>
      <p className="mt-1 text-[var(--foreground-muted)]" data-testid="werkkwaliteit-volgende-moment">
        {geenMeting
          ? t('qa.eigen.geenRapportGeenMeting')
          : toonMoment
            ? t('qa.eigen.geenRapportVolgendeRun', { datum: formatDagDatum(volgendeRun!, locale) })
            : t('qa.eigen.geenRapportVolgendeMoment')}
      </p>
    </div>
  );
}

/**
 * Een geweigerde pagina (403): geen storing, en dus geen rood vlak en geen "probeer het straks nog
 * eens". De meting werkt; deze lezer mag dit rapport niet zien. Zegt het scherm hier "storing", dan
 * stuurt het iemand weg om te wachten op iets dat nooit komt.
 */
export function Geweigerd() {
  const { t } = useTranslation();
  useMeldToestand(t('qa.geweigerd'));

  return (
    <div
      className="rounded-xl border border-[var(--border-soft)] bg-[var(--background)] p-5 text-[0.8125rem] font-semibold text-[var(--foreground)]"
      data-testid="werkkwaliteit-geweigerd"
      role="alert"
    >
      {t('qa.geweigerd')}
    </div>
  );
}

export interface StoringProps {
  /**
   * Wat er misging: de dienst was niet te bereiken, of de meting van deze week is mislukt.
   *
   * Twee verschillende zinnen, want ze zeggen de lezer iets anders. Het eerste geval is nu en gaat
   * vanzelf over; het tweede is deze week, en gaat pas over als er opnieuw gemeten wordt.
   */
  oorzaak: Storingsoorzaak;
  /**
   * De week die deze weergave toont.
   *
   * Komt van de pagina en niet uit het adres: `useGekozenWeek` herstelt een ontbrekende
   * of onbruikbare `?week=` pas in een effect, dus bij de eerste tekening kunnen het
   * adres en de getoonde week uit elkaar lopen. Wie hier het adres zou lezen, biedt in
   * dat ene beeld een link naar de week waar de lezer al staat. En op een scherm zonder
   * weekkiezer staat er helemaal niets in het adres.
   */
  week?: string | null;
  /** De week waar de lezer naartoe kan; `null` wanneer de kiezer er geen eerdere kent. */
  vorigeWeek?: string | null;
  laatsteGeslaagdeRun?: string | null;
  /**
   * De week van de laatste geslaagde meting, zoals het antwoord die noemt.
   *
   * Hier ligt het verschil met `vorigeWeek`: dít is een week waarvan de dienst zegt dat zij het
   * wél deed, en de vorige is alleen de week die in de kiezer onder de huidige staat. Kent het
   * antwoord er een én staat zij in de kiezer, dan wijst de weg terug daarheen.
   */
  laatsteGeslaagdeWeek?: string | null;
  /**
   * De korte foutbeschrijving die de mislukte run heeft achtergelaten, als het antwoord er een
   * draagt. Hij is bij de dienst al geschoond van veldwaarden en namen; dit scherm toont hem
   * ongewijzigd en zet er geen eigen uitleg omheen.
   */
  fout?: string | null;
  /** De weken die de kiezer kent, om te bepalen of de laatste geslaagde week aan te wijzen is. */
  weken?: readonly string[];
  /**
   * Biedt de weergave een knop om het opnieuw te proberen? Alleen waar het opnieuw ophalen werkt:
   * een storing van de dienst is vaak tijdelijk, en een lezer die alleen kan wachten heeft niets.
   */
  onHerlaad?: () => void;
  /**
   * Heeft deze weergave een weg terug?
   *
   * Op een scherm zonder weekkiezer is het antwoord nee: het adres draagt daar geen week,
   * en een link die er een in zet verandert niets — de lezer klikt en blijft staan. Dan
   * hoort er geen link te staan en ook niet de zin dat er geen eerdere week is, want die
   * gaat over de weken en niet over dit scherm.
   */
  terugweg?: boolean;
}

/**
 * Techniek, en hier mag rood: dit is de enige plek in deze module waar dat zo is.
 *
 * De link terug houdt het pad van de weergave waarin de storing staat, zodat een manager die op het
 * persoonsdetail van een collega staat daar ook terugkomt en niet op zijn eigen week belandt.
 */
export function Storing({
  oorzaak,
  week = null,
  vorigeWeek = null,
  laatsteGeslaagdeRun = null,
  laatsteGeslaagdeWeek = null,
  fout = null,
  weken = [],
  terugweg = true,
  onHerlaad,
}: StoringProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const melding =
    oorzaak === STORING_ONBEREIKBAAR ? t('qa.eigen.storingOnbereikbaar') : t('qa.eigen.storingMislukt');
  // De foutbeschrijving hoort ook in de gesproken melding. Zonder haar zegt het
  // meldgebied iets anders dan er op het rode vlak staat, en dan hoort de ene lezer
  // wél wat er misging en de andere niet.
  useMeldToestand(fout === null ? melding : `${melding} ${t('qa.eigen.storingFout', { fout })}`);
  // De weg terug, en de beste van de twee wint. Noemt het antwoord een laatste geslaagde week én
  // kent de kiezer haar, dan gaat de link daarheen: dat is de week waarvan de dienst zegt dat zij
  // het wél deed. Anders blijft het de week onder de huidige, waar dezelfde melding staat zolang
  // ook die week mislukte — de keten eindigt dan vanzelf bij de eerste week die het wel deed.
  //
  // De week die nu op het scherm staat valt af: die is juist de week zonder cijfers, en een link
  // naar zichzelf brengt de lezer nergens.
  const geslaagdeWeek =
    laatsteGeslaagdeWeek !== null &&
    laatsteGeslaagdeWeek !== week &&
    weken.includes(laatsteGeslaagdeWeek)
      ? laatsteGeslaagdeWeek
      : null;
  // Eén plek waar `terugweg` telt, en dat is de JSX hieronder. Hem hier nóg een keer
  // meewegen zou betekenen dat geen van beide controles op zichzelf iets doet — en dan
  // kan er één sneuvelen zonder dat er iets verandert.
  const terugWeek = geslaagdeWeek ?? vorigeWeek;

  return (
    <div
      className="rounded-[10px] bg-[var(--destructive-light)] px-4 py-3 text-[0.8125rem] text-[var(--foreground)]"
      data-testid="werkkwaliteit-storing"
      role="alert"
    >
      <p data-testid="werkkwaliteit-storing-melding">
        <span aria-hidden="true" className="material-symbols-outlined mr-1.5 align-[-3px] text-[16px]">
          error
        </span>
        {melding}
      </p>
      {fout !== null && (
        <p className="mt-1" data-testid="werkkwaliteit-storing-fout">
          {t('qa.eigen.storingFout', { fout })}
        </p>
      )}
      {laatsteGeslaagdeRun !== null && (
        <p className="mt-1" data-testid="werkkwaliteit-storing-laatste-run">
          {t('qa.eigen.storingLaatsteRun', { datum: formatDatum(laatsteGeslaagdeRun, locale) })}
        </p>
      )}
      {onHerlaad !== undefined && (
        <button
          className={`mt-2 rounded-[6px] border border-[var(--border)] bg-[var(--background)] px-3 py-1.5 text-[0.8125rem] font-medium text-[var(--foreground-heading)] max-md:min-h-[2.75rem] ${FOCUSRING}`}
          onClick={onHerlaad}
          type="button"
        >
          {t('qa.eigen.storingOpnieuw')}
        </button>
      )}
      {terugweg && (
      <p className="mt-1">
        {terugWeek === null ? (
          <span data-testid="werkkwaliteit-storing-geen-vorige-week">{t('qa.eigen.storingGeenVorigeWeek')}</span>
        ) : (
          <WeekLink
            data-testid="werkkwaliteit-storing-vorige-week"
            data-werkkwaliteit-terugweg={geslaagdeWeek === null ? 'vorige-week' : 'laatste-geslaagde-week'}
            label={
              geslaagdeWeek === null
                ? t('qa.eigen.storingVorigeWeek', { week: terugWeek })
                : t('qa.eigen.storingLaatsteGeslaagdeWeek', { week: terugWeek })
            }
            week={terugWeek}
          />
        )}
      </p>
      )}
    </div>
  );
}

/**
 * Een week zonder overzicht: nog niet gemeten is iets anders dan een mislukte meting (AD-25). Gedeeld
 * door Team en AI, want beide staan op het medewerkersoverzicht van die week.
 */
export function WeekZonderOverzicht({
  antwoord,
  vorigeWeek,
  week,
  weken,
  onHerlaad,
}: {
  antwoord: Extract<Antwoord<unknown>, { data: null }>;
  vorigeWeek: string | null;
  week: string;
  weken: readonly string[];
  /** Gaat naar de storing, zodat een mislukte meting "Opnieuw proberen" krijgt waar de weergave dat kan. */
  onHerlaad?: () => void;
}) {
  return weektoestand(antwoord.reden) === 'nog_niet_gemeten' ? (
    <GeenRapport
      laatsteGeslaagdeRun={antwoord.laatsteGeslaagdeRun}
      metingIngeschakeld={antwoord.metingIngeschakeld ?? null}
      volgendeRun={antwoord.volgendeRun ?? null}
    />
  ) : (
    <Storing
      fout={antwoord.fout ?? null}
      laatsteGeslaagdeRun={antwoord.laatsteGeslaagdeRun}
      laatsteGeslaagdeWeek={antwoord.laatsteGeslaagdeWeek ?? null}
      onHerlaad={onHerlaad}
      oorzaak={STORING_MISLUKT}
      vorigeWeek={vorigeWeek}
      week={week}
      weken={weken}
    />
  );
}

/**
 * De onderdelen van één beeld lopen uiteen (AD-24). `useEenMeting` haalt één keer per
 * versiecombinatie opnieuw op — meer zou een lus worden. Komt dezelfde combinatie terug, dan staat
 * het scherm stil, en dan hoort de lezer zelf een tweede poging te kunnen doen.
 */
export function MetingHerladen({ onOpnieuw }: { onOpnieuw: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-start gap-2" data-testid="werkkwaliteit-meting-herladen" role="status">
      <p className="text-[0.8125rem] text-[var(--foreground)]">{t('qa.eigen.metingHerladen')}</p>
      <button
        className={`rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-1.5 text-[0.8125rem] font-medium text-[var(--foreground-heading)] hover:bg-[var(--background-muted)] max-sm:min-h-[44px] ${FOCUSRING}`}
        data-testid="werkkwaliteit-meting-opnieuw"
        onClick={onOpnieuw}
        type="button"
      >
        {t('qa.eigen.metingHerladenOpnieuw')}
      </button>
    </div>
  );
}
